import _ from 'lodash';
import {
  COMPARISON_FIELDS, MILITARY_DATE_FIELDS, MILITARY_ENUM_FIELDS, MILITARY_ENUM_MAPPINGS,
} from 'src/actions/AssistanceRequest/Group/utils/constants';

const isBlank = (value) => _.isNil(value) || value === 'undisclosed' || value === 'prefer_not_to_disclose';
const isEqual = (value1, value2) => {
  if (typeof value1 === 'string' && typeof value2 === 'string') {
    return value1.toLowerCase() === value2.toLowerCase();
  }
  if (Array.isArray(value1) && Array.isArray(value2)) {
    return _.isEqual(value1.sort(), value2.sort());
  }

  return _.isEqual(value1, value2);
};
const getDifferences = (array1, array2, keys) => _.differenceWith(array1, array2, (a, b) => keys.every((key) => {
      const valueA = _.get(a, key);
      const valueB = _.get(b, key);

      return isEqual(valueA, valueB);
    }));

const getUniqueItems = (arr, uniqueKeys) => {
  const comparisonFunction = (a, b) => uniqueKeys.every((key) => _.isEqual(_.get(a, key), _.get(b, key)));

  return _.uniqWith(arr, comparisonFunction);
};

const sortByKeyWithPriority = (arr, key, orderPriority) => _.sortBy(arr, (item) => orderPriority[item[key]]);

const compareFields = (targetFields, sourceObj, targetObj, military = false) => {
  const result = {};

  targetFields.forEach((targetField) => {
    const sourceField = military ? (MILITARY_ENUM_MAPPINGS[targetField] || targetField) : targetField;
    const sourceValue = sourceObj?.[sourceField];
    const targetValue = targetObj?.[targetField];
    const fieldChanged = (isBlank(sourceValue) && !isBlank(targetValue)) ||
                          (!isBlank(targetValue) && !isEqual(sourceValue, targetValue));

    if (fieldChanged) {
      result[sourceField] = [sourceValue, targetValue];
    }
  });

  return result;
};

const getDifferencesWithoutDuplicates = (sourceArray, targetArray, keyArray) => getDifferences(
    getUniqueItems(sourceArray, keyArray),
    targetArray,
    keyArray,
  );

const setIsPrimaryFlag = (array) => array.map((item, index) => {
      if (index === 0) {
        return {
          ...item,
          is_primary: true,
        };
      }
      return item;
    });

export const getUpdatedValues = (changes) => Object.fromEntries(
  Object.entries(changes).map(([key, value]) => [key, value[1]]),
);

const getMilitaryChanges = (existingPerson = {}, assistanceRequestPerson = {}) => {
  let militaryChanges = {};

  // assistance request military fields
  const militaryFields = [...MILITARY_ENUM_FIELDS, ...MILITARY_DATE_FIELDS, ...Object.keys(MILITARY_ENUM_MAPPINGS)];
  const changes = compareFields(militaryFields, existingPerson.military, assistanceRequestPerson.military, true);
  const existingAffiliation = existingPerson.military?.affiliation;
  const newAffiliation = assistanceRequestPerson.military?.affiliation;
  const affiliationChanged = !_.isNil(existingAffiliation) &&
                              !_.isNil(newAffiliation) &&
                              !isEqual(existingAffiliation, newAffiliation);
  const addedAffiliation = _.isNil(existingAffiliation) && !_.isNil(newAffiliation);

  if (affiliationChanged || addedAffiliation) {
    changes.affiliation = [existingPerson.military?.affiliation, assistanceRequestPerson.military?.affiliation];
  }

  if (!_.isEmpty(changes)) {
    const newValues = getUpdatedValues(changes);

    militaryChanges = {
      military: [
        existingPerson.military,
        {
          ...existingPerson.military,
          ...newValues,
        },
      ],
    };
  }

  return militaryChanges;
};

const getSexualityChanges = (existingPerson = {}, assistanceRequestPerson = {}) => {
  if (assistanceRequestPerson.sexuality?.length === 0) {
    return {};
  }

  const changes = compareFields(['sexuality', 'sexuality_other'], existingPerson, assistanceRequestPerson);
  if ((!assistanceRequestPerson.sexuality?.includes('other')) && !_.isEmpty(existingPerson.sexuality_other)) {
    changes.sexuality_other = [existingPerson.sexuality_other, null];
  } else if (!assistanceRequestPerson.sexuality?.includes('other') && _.isEmpty(existingPerson.sexuality_other)) {
    delete changes.sexuality_other;
  }

  return changes;
};

const getRecordLanguagesChanges = (existingPerson = {}, assistanceRequestPerson = {}) => {
  const sameLanguage = (a, b) => a.record_language_type === b.record_language_type &&
    a.language_id === b.language_id;

  const existingLanguages = existingPerson.preferred_languages || [];
  const arLanguages = assistanceRequestPerson.preferred_languages || [];

  if (existingLanguages.length === 0 && arLanguages.length === 0) {
    return {};
  }

  const onlyInARLanguages = arLanguages
    .filter((arLanguage) => !existingLanguages.some((existingLanguage) => sameLanguage(existingLanguage, arLanguage)));

  if (onlyInARLanguages.length > 0) {
    return {
      record_languages: [existingLanguages, arLanguages],
    };
  }

  return {};
};

function getDifferencesByAddresses(sourceArray = [], targetArray = []) {
  const normalizeLine2 = (addr) => (addr.line_2 ? addr.line_2.toLowerCase().trim() : '');
  const uniqueAddressKeys = ['latitude', 'longitude', 'address_type', 'normalizedLine2'];

  const sourceNormalized = sourceArray.map((addr) => ({
    ...addr,
    normalizedLine2: normalizeLine2(addr),
  }));

  const targetNormalized = targetArray.map((addr) => ({
    ...addr,
    normalizedLine2: normalizeLine2(addr),
  }));

  return getDifferences(
    getUniqueItems(sourceNormalized, uniqueAddressKeys),
    targetNormalized,
    uniqueAddressKeys,
  ).map((addr) => ({ ...addr, normalizedLine2: undefined }));
}

const getPersonChanges = (existingPerson = {}, assistanceRequestPerson = {}) => {
  const sexualityChanges = getSexualityChanges(existingPerson, assistanceRequestPerson);
  const recordLanguagesChanges = getRecordLanguagesChanges(existingPerson, assistanceRequestPerson);
  const militaryChanges = getMilitaryChanges(existingPerson, assistanceRequestPerson);

  const personChanges = {
    updates: {
      ...compareFields(COMPARISON_FIELDS, existingPerson, assistanceRequestPerson),
      ...sexualityChanges,
      ...recordLanguagesChanges,
      ...militaryChanges,
    },
    additions: {
      phone_numbers: getDifferencesWithoutDuplicates(
        sortByKeyWithPriority(
          assistanceRequestPerson.phone_numbers,
          'phone_type',
          {
            mobile: 1, home: 2, unknown: 3, work: 4, fax: 5,
          },
        ),
        existingPerson.phone_numbers,
        ['phone_number', 'country_code'],
      ),
      email_addresses: getDifferencesWithoutDuplicates(
        assistanceRequestPerson.email_addresses,
        existingPerson.email_addresses,
        ['email_address'],
      ),
      addresses: getDifferencesByAddresses(
        assistanceRequestPerson.addresses,
        existingPerson.addresses,
      ),
    },
  };

  // Set is_primary to true for additions
  personChanges.additions.phone_numbers = setIsPrimaryFlag(personChanges.additions.phone_numbers);
  personChanges.additions.email_addresses = setIsPrimaryFlag(personChanges.additions.email_addresses);
  personChanges.additions.addresses = setIsPrimaryFlag(personChanges.additions.addresses);

  return personChanges;
};

export default getPersonChanges;
